import { useCallback, useMemo, useState } from 'react'
import { useTeacherRegistry } from '@/shared/api/hooks/useTeacherRegistry'
import { inTeacherScope, useTeacherScope } from '@/shared/api/hooks/useTeacherScope'
import {
    DUPLICATES_POLL_MS,
    useMyDuplicates,
    usePendingDuplicates
} from '@/shared/api/hooks/useParentDuplicates'
import {
    getFromLocalStorage,
    saveToLocalStorage
} from '@/shared/utils/localStorageHelper'
import { getTeacherName, normalizeStatus } from '@/pages/user/teacher/teachers/types'
import { formatScore } from '@/pages/user/teacher/duplicates/types'

/** Ids the user has already seen, persisted so the badge doesn't nag on reload. */
const READ_STORAGE_KEY = 'readNotificationIds'

/** Poll interval for the derived feed — the backend exposes no push channel. */
const POLL_INTERVAL_MS = 60_000

export type AppNotificationType = 'teacher-request' | 'duplicate-parent'

export type AppNotification = {
    id: string
    type: AppNotificationType
    title: string
    description: string
    href: string
    createdAt?: string | null
    isRead: boolean
}

/**
 * Derive the in-app notification feed.
 *
 * There is no notifications resource on the backend, so the feed is computed
 * from pollable data. Two sources today:
 *
 *   - Pending teacher requests (admin teachers only).
 *   - Suspected duplicate parent accounts. Admins see every unreviewed pair in
 *     the church; everyone else sees only the pairs they are personally part of,
 *     which is what puts "suspected duplicate account" in front of a parent the
 *     moment they log in.
 *
 * Read state lives in localStorage, which `AuthContext.logout()` clears along
 * with everything else.
 */
export const useNotifications = () => {
    // One rule, shared with the teacher registry: a branch admin is nudged
    // about their own branch, a global admin about the whole church. The two
    // disagreeing is what made the sidebar badge and the registry's own count
    // report different numbers for the same backlog.
    const { isAdminTeacher, scopeBranchId, ready: scopeReady } = useTeacherScope()

    const [readIds, setReadIds] = useState<string[]>(
        () => getFromLocalStorage<string[]>(READ_STORAGE_KEY) ?? []
    )

    // Only admin teachers may call /teachers — gating the query keeps parents
    // and regular teachers from firing a request that would 403. Polling stands
    // in for the push channel the backend doesn't offer.
    const { teachers, loading } = useTeacherRegistry({
        enabled: isAdminTeacher,
        refetchInterval: POLL_INTERVAL_MS
    })

    // The two duplicate sources are mutually exclusive so an admin who is also a
    // parent doesn't see their own pair twice: the church-wide list already
    // contains it.
    const churchDuplicates = usePendingDuplicates({
        enabled: isAdminTeacher,
        refetchInterval: DUPLICATES_POLL_MS
    })
    const ownDuplicates = useMyDuplicates({
        enabled: !isAdminTeacher,
        refetchInterval: DUPLICATES_POLL_MS
    })

    const { notifications: teacherItems, otherBranchCount } = useMemo(() => {
        // Until the admin's own branch has resolved, an empty feed is the
        // honest answer: showing every branch's requests and then removing
        // most of them a moment later is worse than showing none.
        if (!isAdminTeacher || !scopeReady)
            return { notifications: [], otherBranchCount: 0 }

        const pending = teachers.filter(
            (teacher) => normalizeStatus(teacher.status) === 'pending'
        )

        const inScope = pending.filter((teacher) =>
            inTeacherScope(teacher, scopeBranchId)
        )

        const items = inScope.map<AppNotification>((teacher) => {
            const id = `teacher-request:${teacher._id}`
            return {
                id,
                type: 'teacher-request' as const,
                title: `${getTeacherName(teacher)} requested teacher access`,
                description: teacher.branch?.name
                    ? `Awaiting approval for ${teacher.branch.name}`
                    : 'Awaiting approval',
                // The id targets the exact request. Without it the registry
                // lands on the pending filter and auto-selects the first row,
                // so the wrong teacher is one click from being approved.
                href: `/user/teacher/teachers?status=pending&teacherId=${teacher._id}`,
                createdAt: teacher.createdAt ?? null,
                isRead: readIds.includes(id)
            }
        })

        return {
            notifications: items,
            // Surfaced in the UI so scoped-out requests are never invisible.
            otherBranchCount: pending.length - inScope.length
        }
    }, [isAdminTeacher, scopeReady, teachers, scopeBranchId, readIds])

    const duplicateItems = useMemo(() => {
        const records = isAdminTeacher
            ? churchDuplicates.duplicates
            : // `by-parent` returns resolved pairs too; only unreviewed ones are
              // something the parent still has to act on.
              ownDuplicates.duplicates.filter((record) => !record.processed)

        return records.map<AppNotification>((record) => {
            const id = `duplicate-parent:${record._id}`
            return {
                id,
                type: 'duplicate-parent' as const,
                title: isAdminTeacher
                    ? 'Suspected duplicate registration'
                    : 'Suspected duplicate account',
                description: isAdminTeacher
                    ? `${formatScore(record.similarityScore)} match · ${
                          record.duplicateReason || 'flagged by detection'
                      }`
                    : 'Two accounts share your name and phone number — choose which to keep.',
                // Admins land on the pair they were told about; a parent's own
                // review page holds at most a couple of pairs, so it needs no
                // targeting.
                href: isAdminTeacher
                    ? `/user/teacher/duplicate-registration?duplicateId=${record._id}`
                    : '/user/parent/duplicates',
                createdAt: record.createdAt ?? null,
                isRead: readIds.includes(id)
            }
        })
    }, [isAdminTeacher, churchDuplicates.duplicates, ownDuplicates.duplicates, readIds])

    const notifications = useMemo(
        () =>
            [...teacherItems, ...duplicateItems].sort((a, b) => {
                if (a.isRead !== b.isRead) return a.isRead ? 1 : -1
                return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
            }),
        [teacherItems, duplicateItems]
    )

    const persist = useCallback((ids: string[]) => {
        setReadIds(ids)
        saveToLocalStorage(READ_STORAGE_KEY, ids)
    }, [])

    const markRead = useCallback(
        (id: string) => {
            if (readIds.includes(id)) return
            persist([...readIds, id])
        },
        [readIds, persist]
    )

    const markAllRead = useCallback(() => {
        const ids = notifications.map((notification) => notification.id)
        persist([...new Set([...readIds, ...ids])])
    }, [notifications, readIds, persist])

    return {
        notifications,
        otherBranchCount,
        unreadCount: notifications.filter((n) => !n.isRead).length,
        markRead,
        markAllRead,
        isAdminTeacher,
        /**
         * Whether this user has a feed at all. Parents now do — duplicate
         * account reviews land in it — so UI that used to hide itself behind
         * `isAdminTeacher` should key off this instead.
         */
        hasFeed: isAdminTeacher || duplicateItems.length > 0,
        loading: loading || churchDuplicates.loading || ownDuplicates.loading
    }
}
